import { useEffect, useState } from "react";

/**
 * In-app loading screen shown during the boot sequence. The brand splash
 * (per-aspect variant picked by media-query) fills the viewport. Below the
 * splash sits a progress bar that linearly fills 0 → 100 % over the boot-
 * hold window (MIN_BOOT_HOLD_MS), plus the live release version injected
 * at build time via `vite.config.ts` `define`.
 *
 * The fill is animated via a CSS transition triggered by a state change
 * on next-frame: render at 0 %, then on the next requestAnimationFrame
 * flip to 100 %. React + the browser interpolate the width over the
 * transition duration. No keyframes, no charting lib, no JS rAF loop.
 */

// Module-level timestamp captured the first time this module is imported —
// i.e. when the React app starts loading. Used by `useBootHold()` to enforce
// a minimum visible-splash window even on a fast-localhost boot.
const APP_BOOT_AT = Date.now();
const MIN_BOOT_HOLD_MS = 4000;

/**
 * Returns true until at least MIN_BOOT_HOLD_MS has elapsed since the module
 * first loaded. Used by `<AuthBootstrap>` to keep the splash visible even
 * if auth + hydration resolve in <50 ms (the localhost case).
 *
 * After the hold elapses, the hook returns false forever in this session;
 * subsequent route mounts don't see the splash a second time.
 */
export function useBootHold(): boolean {
  const remainingAtMount = MIN_BOOT_HOLD_MS - (Date.now() - APP_BOOT_AT);
  const [held, setHeld] = useState(remainingAtMount > 0);
  useEffect(() => {
    if (!held) return;
    if (remainingAtMount <= 0) {
      setHeld(false);
      return;
    }
    const t = setTimeout(() => setHeld(false), remainingAtMount);
    return () => clearTimeout(t);
  }, [held, remainingAtMount]);
  return held;
}

interface AppLoadingScreenProps {
  /** German status text below the progress bar. Visually small — the user's
   *  attention is on the brand splash, not the status line. */
  label?: string;
}

export function AppLoadingScreen({ label }: AppLoadingScreenProps) {
  // Start at 0 %, flip to 100 % on next paint. The inline `transition`
  // interpolates the width over MIN_BOOT_HOLD_MS — no keyframes needed.
  const [fill, setFill] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setFill(100));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label ?? "App wird geladen"}
      data-testid="app-loading-screen"
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: "#1a2f5e" }}
    >
      {/*
        Five splash variants — one per device class. Browser picks the matching
        <source> via media-queries, so the asset's native aspect ratio already
        fills the viewport without cover/contain trickery. Order matters:
        most-specific first (landscape ≥1024), broadest last (portrait <768
        falls through to the <img> default).
      */}
      <div className="relative w-full h-full">
        <picture>
          <source media="(orientation: landscape) and (min-width: 1024px)" srcSet="/brand/splash-desktop.png" />
          <source media="(orientation: landscape) and (min-width: 768px)" srcSet="/brand/splash-tablet-landscape.png" />
          <source media="(orientation: landscape)" srcSet="/brand/splash-landscape.png" />
          <source media="(orientation: portrait) and (min-width: 768px)" srcSet="/brand/splash-tablet-portrait.png" />
          <img
            src="/brand/splash.png"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="loading-splash-img"
          />
        </picture>
        <div className="absolute inset-x-0 bottom-[12%] flex flex-col items-center gap-3 px-8 pointer-events-none">
          <div
            className="h-1 w-full max-w-[280px] rounded-full overflow-hidden"
            style={{ background: "rgba(255,255,255,0.30)" }}
            data-testid="loading-progress-track"
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${fill}%`,
                transition: `width ${MIN_BOOT_HOLD_MS}ms linear`,
                background:
                  "linear-gradient(90deg, #ffffff 0%, #c8202c 60%, #ffffff 100%)",
              }}
              data-testid="loading-progress-fill"
              aria-valuenow={fill}
              aria-valuemin={0}
              aria-valuemax={100}
              role="progressbar"
            />
          </div>
          {label && (
            <p
              className="text-[11px] text-white/90 tracking-wide text-center"
              data-testid="loading-label"
            >
              {label}
            </p>
          )}
          <p
            className="text-[10px] text-white/75 tracking-[0.18em] font-mono"
            data-testid="loading-version"
          >
            v{__APP_VERSION__} · BUILD {__BUILD_DATE__}
          </p>
        </div>
      </div>
    </div>
  );
}
